import axios from "axios";
import { Client, ClientLedger, ClientVisit } from "../interfaces/ClientInterface";
import { Get, Post, Delete, Put } from "./apiHelper";
import { TBranchClients } from "../containers/Client/SearchOtherBranchClients";
import { AttachmentInterface } from "../interfaces/AttachmentInterface";

const clientRoot = "/api/clients";

const clientsRoot = "/api/clients";

export async function fetchClientByReferenceId(referenceId: number): Promise<Client> {
  try {
    const response = await axios.get(`${clientRoot}/reference/${referenceId}`);
    return response.data as Client;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    return err;
  }
}

export async function batchUploadClient(data: Record<string, unknown>): Promise<unknown> {
  const response = await Post(`${clientsRoot}/batch`, { data }, true);
  return response.data;
}

export async function searchMuldartaClients(query: string): Promise<Client[]> {
  const response = await Get(`/api/searchMulDartaClient?q=${query}`, true);
  return response.data as Client[];
}

export async function getClientDetails(id: number): Promise<Client> {
  const response = await Get(`${clientsRoot}/${id}`, true);
  return response.data as Client;
}

export async function getBatchClient(query: Record<string, string>): Promise<unknown> {
  const response = await Get(`${clientsRoot}`, true, { ...query });
  return response.data;
}

export async function createClient(clientData: Partial<Client>): Promise<Client> {
  const response = await Post(`${clientsRoot}`, clientData, true);
  return response.data as Client;
}

export async function createBatchClient(clientsData: Record<string, unknown>): Promise<unknown> {
  return Post(`${clientsRoot}/batch`, clientsData, true);
}

export async function updateClient(id: number, clientData: Partial<Client>): Promise<Client> {
  const response = await Put(`${clientsRoot}/${id}`, clientData, true);
  return response.data as Client;
}

export async function updateClientCustomer(
  id: number,
  customerData: Record<string, unknown>
): Promise<unknown> {
  const response = await Put(`${clientsRoot}/${id}/customer`, customerData, true);
  return response.data;
}

export const getClientVisits = async (id: number): Promise<Array<ClientVisit>> => {
  const response = await Get(`${clientsRoot}/${id}/visits`, true);
  return response.data as Array<ClientVisit>;
};

export const deleteClient = async (id: number): Promise<unknown> => {
  const response = await Delete(`${clientsRoot}/${id}`, true);
  return response.data;
};

export const getS3UploadPostData = async (fileName: string): Promise<unknown> => {
  const response = await Post(`${clientsRoot}/profileImage`, { fileName });
  return response.data;
};

export const uploadClientPicture = async (id: number, pictureData: Blob): Promise<unknown> => {
  const fileName = `${id}.${pictureData.type.split("/")[1]}`;
  const postData = (await getS3UploadPostData(fileName)) as {
    id: number;
    data: Record<string, unknown> & { fields: Record<string, string>; url: string };
  };

  const s3FileName = `${postData.id}/${fileName}`;

  const formData = new FormData();

  Object.entries(postData.data.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });

  formData.append("file", pictureData, s3FileName);

  const response = await axios.post(postData.data.url, formData);

  await updateClientCustomer(id, {
    customerDetails: { profileImageS3Url: response.headers.location }
  });
  return { clientId: id, profileImageS3Url: response.headers.location };
};

export async function getClientLedger(clientId: number): Promise<ClientLedger[]> {
  const response = await Get(`/api/clientLedger/${clientId}`, true);
  return response.data as ClientLedger[];
}

export async function getWalkInClient(resourceCentreId: number): Promise<Client> {
  const response = await Get(`${clientRoot}/${resourceCentreId}/walkInCustomer`, true);
  return response.data as Client;
}

export async function getClientsWithPhone({ phone }: { phone: string }): Promise<Array<Client>> {
  const { data } = await Get(`${clientsRoot}/phone?phone=${phone || ""}`, true);
  return data as Array<Client>;
}

export async function getPublicClientDetail({ id }: { id: number }): Promise<Array<Client>> {
  const { data } = await Get(`${clientsRoot}/public/${id}`, true);
  return data as Array<Client>;
}

export async function createPublicClient({ clientId }: { clientId: number }): Promise<Client> {
  const { data } = await Post(`${clientsRoot}/public`, { clientId }, true);
  return data as Client;
}

export async function sendAppLinkToClient(
  id: number
): Promise<{ success: boolean; message: string; client: Client }> {
  const { data } = await Post(`${clientsRoot}/${id}/sendAppLink`, {}, true);
  return data as { success: boolean; message: string; client: Client };
}

// get branch clients
export async function getBranchClients({
  resourceCentreId,
  searchBy,
  searchValue
}: {
  resourceCentreId: number;
  searchValue: string;
  searchBy: string;
}): Promise<Array<TBranchClients>> {
  const query = new URLSearchParams();
  query.append("searchValue", searchValue);
  query.append("searchBy", searchBy);
  query.append("resourceCentreId", resourceCentreId.toString());

  const response = await Get(`${clientsRoot}/branchClients?${query.toString()}`, true);
  return response.data as Array<TBranchClients>;
}

// ---------- insurance image apis start ----------

export interface InsuranceImageInfo {
  s3ResourceURL: string;
  s3ResourceKey: string;
  fileName: string;
}

// Step 1: get presigned S3 POST data for insurance image upload
export const getPresignedPostDataForInsuranceImage = async (fileName: string): Promise<unknown> => {
  const response = await Post(`${clientsRoot}/insuranceImage`, { fileName }, true);
  return response.data;
};

// Step 2: upload the blob directly to S3 and return image info (key and url)
export const uploadClientInsuranceImage = async (imageData: Blob): Promise<InsuranceImageInfo> => {
  const fileName = `${Date.now()}.${imageData.type.split("/")[1]}`;

  const postData = (await getPresignedPostDataForInsuranceImage(fileName)) as {
    id: number;
    data: Record<string, unknown> & { fields: Record<string, string>; url: string };
  };

  const s3FileName = `${postData.id}/${fileName}`;

  const formData = new FormData();
  Object.entries(postData.data.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", imageData, s3FileName);

  const response = await axios.post(postData.data.url, formData, {
    headers: { "Content-Type": "application/octet-stream" }
  });

  const imageUrl =
    response.headers.location || `${postData.data.url}/${postData.data.fields.key}` || "";

  const imageKey = postData.data.fields.key as string;

  return { s3ResourceURL: imageUrl, s3ResourceKey: imageKey, fileName };
};

// Step 3: persist the uploaded image info to the client record
export const saveClientInsuranceImage = async (
  clientId: number,
  imageInfo: InsuranceImageInfo
): Promise<AttachmentInterface> => {
  const response = await Post(
    `${clientsRoot}/${clientId}/insuranceImage`,
    imageInfo as unknown as Record<string, unknown>,
    true
  );

  return response.data as AttachmentInterface;
};

// Delete a previously uploaded insurance image by its uuid
export const deleteClientInsuranceImage = async (id: number): Promise<void> => {
  await Delete(`${clientsRoot}/insuranceImage/${id}`, true);
};

// ---------- insurance image apis end ----------
